import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { usersAPI, teamsAPI, tasksAPI, projectsAPI, timeLogsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Card, PageHeader, Badge, Button, Modal, FormField, Table, Empty, Alert, Loading, Avatar, PasswordInput } from '../components/ui';
import { Pencil, Trash2, UserPlus, UsersRound, Search, Crown, X, Mail, Phone, Calendar, Briefcase, FolderKanban, CheckSquare, Clock, ChevronRight, LayoutGrid, List, Hash, FileText } from 'lucide-react';
import './Users.css';

const ROLE_COLORS = {
  Admin: '#c084fc',
  Manager: '#06d6a0',
  Member: '#60a5fa',
  Client: '#ffd166',
};

const ROLES = ['Admin', 'Manager', 'Member', 'Client'];

const getInitials = (name = '') =>
  name.split(' ').filter(Boolean).slice(0, 2).map(p => p[0]?.toUpperCase()).join('') || '?';

const projectMemberIds = (project) => {
  if (Array.isArray(project.members)) return project.members.map(Number);
  try { return JSON.parse(project.members || '[]').map(Number); } catch { return []; }
};

const userInProject = (project, userId) => {
  const id = Number(userId);
  return Number(project.project_lead_id) === id
    || Number(project.manager_id) === id
    || Number(project.client_id) === id
    || projectMemberIds(project).includes(id);
};

const getUserProjectRole = (project, userId) => {
  const id = Number(userId);
  if (Number(project.project_lead_id) === id) return 'Project Lead';
  if (Number(project.manager_id) === id) return 'Manager';
  if (Number(project.client_id) === id) return 'Client';
  if (projectMemberIds(project).includes(id)) return 'Member';
  return 'Contributor';
};

export default function Users() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [teamModal, setTeamModal] = useState(false);
  const [createTeamModal, setCreateTeamModal] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', role: 'Member', title: '', gender: '', rating: 0 });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const { user: me, canCreate, canUpdate, canDelete } = useAuth();
  const [roleFilter, setRoleFilter] = useState('');
  const [search, setSearch] = useState('');
  const [teamSearch, setTeamSearch] = useState('');
  const [teamViewMode, setTeamViewMode] = useState('tiles');

  const load = async () => {
    setLoading(true);
    try {
      const [usersResult, teamsResult] = await Promise.allSettled([
        usersAPI.list(),
        teamsAPI.list(),
      ]);

      setUsers(usersResult.status === 'fulfilled' ? usersResult.value.data.data || [] : []);
      setTeams(teamsResult.status === 'fulfilled' ? teamsResult.value.data.data || [] : []);

      const failure = [usersResult, teamsResult].find(
        result => result.status === 'rejected' && result.reason?.response?.status !== 403
      );
      setError(failure ? failure.reason?.response?.data?.message || 'Unable to load team data.' : '');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name, email: u.email, role: u.role, title: u.title, gender: u.gender || '', rating: u.rating || 0 });
    setError('');
    setModal(true);
  };

  const openTeamModal = () => {
    setEditing(null);
    setForm({ name: '', email: '', role: 'Member', title: '', gender: '', rating: 0 });
    setError('');
    setTeamModal(true);
  };

  const openCreateTeam = () => {
    setCreateTeamModal(true);
    setTeamForm({ name: '', description: '', code: '', memberIds: [], team_lead_id: '' });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await usersAPI.update(editing.user_id, form);
      setModal(false);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Error');
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await usersAPI.create(form);
      setTeamModal(false);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Error');
    } finally {
      setSaving(false);
    }
  };

  const [teamForm, setTeamForm] = useState({ name: '', description: '', code: '', memberIds: [], team_lead_id: '' });
  const [teamSaving, setTeamSaving] = useState(false);

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    setTeamSaving(true);
    try {
      const body = {
        name: teamForm.name,
        description: teamForm.description,
        code: teamForm.code,
        memberIds: teamForm.memberIds,
        team_lead_id: teamForm.team_lead_id ? Number(teamForm.team_lead_id) : null,
      };
      await teamsAPI.create(body);
      setCreateTeamModal(false);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Error');
    } finally {
      setTeamSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (id === me?.user_id) {
      alert("You can't delete yourself.");
      return;
    }
    if (!window.confirm('Delete this user?')) return;
    await usersAPI.delete(id);
    load();
  };

  const toggleRoleFilter = (role) => setRoleFilter(prev => (prev === role ? '' : role));

  const openUserDetail = (user) => {
    navigate(`/app/users/${user.user_id}`);
  };

  const roleGroups = ROLES.map(role => ({
    role,
    count: users.filter(u => u.role === role).length,
    color: ROLE_COLORS[role],
  }));

  const filteredUsers = users.filter(u => {
    const matchesRole = !roleFilter || u.role === roleFilter;
    const q = search.trim().toLowerCase();
    const matchesSearch = !q
      || u.name?.toLowerCase().includes(q)
      || u.email?.toLowerCase().includes(q)
      || u.title?.toLowerCase().includes(q);
    return matchesRole && matchesSearch;
  });

  const filteredTeams = teams.filter(t => {
    const q = teamSearch.trim().toLowerCase();
    return !q || t.name?.toLowerCase().includes(q) || t.code?.toLowerCase().includes(q);
  });

  const columns = [
    {
      key: 'name',
      label: 'Member',
      render: r => (
        <div className="users-page__user-cell">
          <Avatar
            name={r.name}
            size="md"
            color={r.user_id === me?.user_id ? 'var(--accent)' : ROLE_COLORS[r.role] || 'var(--accent)'}
            style={{ borderRadius: '50%' }}
          />
          <div className="users-page__user-info">
            <div className="users-page__user-name">
              {r.name}
              {r.user_id === me?.user_id && <span className="users-page__you-badge">You</span>}
            </div>
            <div className="users-page__user-email">{r.email}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'designation',
      label: 'Designation',
      render: r => r.title
        ? <span className="users-page__cell-muted">{r.title}</span>
        : <span className="users-page__cell-empty">Not set</span>,
    },
    {
      key: 'gender',
      label: 'Gender',
      render: r => r.gender
        ? <span className="users-page__cell-muted">{r.gender}</span>
        : <span className="users-page__cell-empty">—</span>,
    },
    {
      key: 'rating',
      label: 'Rating',
      render: r => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ color: '#fbbf24', fontSize: '14px' }}>★</span>
          <span style={{ fontWeight: 600, color: 'var(--text)', fontSize: '13px' }}>{r.rating || '0.0'}</span>
        </div>
      ),
    },
    { key: 'role', label: 'Role', render: r => <Badge value={r.role} /> },
    {
      key: 'createdAt',
      label: 'Joined',
      render: r => (
        <span className="users-page__cell-muted">
          {new Date(r.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        </span>
      ),
    },
    {
      key: 'actions',
      label: '',
      render: r => (
        canUpdate || canDelete ? (
          <div className="users-page__row-actions">
            {canUpdate && (
              <Button size="sm" variant="ghost" onClick={e => { e.stopPropagation(); openEdit(r); }} aria-label="Edit">
                <Pencil size={14} />
              </Button>
            )}
            {canDelete && (
              <Button
                size="sm"
                variant="danger"
                onClick={e => { e.stopPropagation(); handleDelete(r.user_id); }}
                disabled={r.user_id === me?.user_id}
                aria-label="Delete"
              >
                <Trash2 size={14} />
              </Button>
            )}
          </div>
        ) : null
      ),
    },
  ];

  // Donut chart
  const total = users.length;
  const PIE_R = 54;
  const PIE_CX = 70;
  const PIE_CY = 70;
  let cumAngle = -90;
  const activeGroups = roleGroups.filter(g => g.count > 0);
  const pieSlices = activeGroups.map(g => {
    const pct = total > 0 ? g.count / total : 0;
    const startAngle = cumAngle;
    cumAngle += pct * 360;
    const endAngle = cumAngle;
    const large = pct > 0.5 ? 1 : 0;
    const toRad = deg => (deg * Math.PI) / 180;
    const sx = PIE_CX + PIE_R * Math.cos(toRad(startAngle));
    const sy = PIE_CY + PIE_R * Math.sin(toRad(startAngle));
    const ex = PIE_CX + PIE_R * Math.cos(toRad(endAngle));
    const ey = PIE_CY + PIE_R * Math.sin(toRad(endAngle));
    return { ...g, pct, d: `M ${PIE_CX} ${PIE_CY} L ${sx} ${sy} A ${PIE_R} ${PIE_R} 0 ${large} 1 ${ex} ${ey} Z` };
  });

  const headerActions = canCreate ? (
    <div className="users-page__actions">
      <Button variant="secondary" onClick={openTeamModal}>
        <UserPlus size={15} /> Add Member
      </Button>
      <Button onClick={openCreateTeam} disabled={users.length === 0}>
        <UsersRound size={15} /> Create Team
      </Button>
    </div>
  ) : null;

  return (
    <div className="users-page animate-in">
      <PageHeader
        title="Team"
        subtitle={`${users.length} member${users.length !== 1 ? 's' : ''} across your workspace`}
        eyebrow="People"
        action={headerActions}
      />

      {/* Role stat cards */}
      <div className="users-page__stats">
        <button
          type="button"
          className={`users-page__stat${roleFilter === '' ? ' users-page__stat--active' : ''}`}
          style={{ '--stat-color': 'var(--accent)' }}
          onClick={() => setRoleFilter('')}
        >
          <div className="users-page__stat-glow" />
          <div className="users-page__stat-label">Total</div>
          <div className="users-page__stat-value">{users.length}</div>
          <div className="users-page__stat-bar">
            <div className="users-page__stat-bar-fill" style={{ width: '100%' }} />
          </div>
        </button>
        {roleGroups.map(g => (
          <button
            key={g.role}
            type="button"
            className={`users-page__stat${roleFilter === g.role ? ' users-page__stat--active' : ''}`}
            style={{ '--stat-color': g.color }}
            onClick={() => toggleRoleFilter(g.role)}
          >
            <div className="users-page__stat-glow" />
            <div className="users-page__stat-label">{g.role}</div>
            <div className="users-page__stat-value">{g.count}</div>
            <div className="users-page__stat-bar">
              <div
                className="users-page__stat-bar-fill"
                style={{ width: total ? `${(g.count / total) * 100}%` : '0%' }}
              />
            </div>
          </button>
        ))}
      </div>

      {/* Analytics + search toolbar */}
      <Card className="users-page__toolbar" glass>
        <div className="users-page__toolbar-inner">
          <div className="users-page__chart-panel">
            <div className="users-page__chart-wrap">
              <svg width={140} height={140} aria-hidden="true">
                {pieSlices.map(s => (
                  <path
                    key={s.role}
                    d={s.d}
                    fill={s.color}
                    opacity={roleFilter === s.role ? 1 : roleFilter ? 0.35 : 0.9}
                    stroke="var(--bg-card)"
                    strokeWidth={2}
                    style={{ cursor: 'pointer', transition: 'opacity 0.2s' }}
                    onClick={() => toggleRoleFilter(s.role)}
                  />
                ))}
                <circle cx={PIE_CX} cy={PIE_CY} r={28} fill="var(--bg-card)" />
              </svg>
              <div className="users-page__chart-center">
                <span className="users-page__chart-value">{filteredUsers.length}</span>
                <span className="users-page__chart-label">{roleFilter || 'All'}</span>
              </div>
            </div>
            <div className="users-page__legend">
              {activeGroups.map(g => (
                <div
                  key={g.role}
                  className={`users-page__legend-item${roleFilter && roleFilter !== g.role ? ' users-page__legend-item--dim' : ''}`}
                  onClick={() => toggleRoleFilter(g.role)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => e.key === 'Enter' && toggleRoleFilter(g.role)}
                >
                  <span className="users-page__legend-dot" style={{ background: g.color }} />
                  <span className="users-page__legend-name">{g.role}</span>
                  <span className="users-page__legend-count">{g.count}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="users-page__controls">
            <div className="users-page__search-wrap">
              <Search size={16} className="users-page__search-icon" />
              <input
                className="users-page__search"
                placeholder="Search by name, email, or designation..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <div className="users-page__role-filters">
              <button
                type="button"
                className={`users-page__role-pill${roleFilter === '' ? ' users-page__role-pill--active' : ''}`}
                style={roleFilter === '' ? { background: 'linear-gradient(135deg, var(--accent), var(--accent-light))', '--pill-color': 'var(--accent)' } : {}}
                onClick={() => setRoleFilter('')}
              >
                All
                <span className="users-page__role-pill-count">{users.length}</span>
              </button>
              {roleGroups.filter(g => g.count > 0).map(g => (
                <button
                  key={g.role}
                  type="button"
                  className={`users-page__role-pill${roleFilter === g.role ? ' users-page__role-pill--active' : ''}`}
                  style={roleFilter === g.role ? { background: g.color, '--pill-color': g.color } : {}}
                  onClick={() => toggleRoleFilter(g.role)}
                >
                  {g.role}
                  <span className="users-page__role-pill-count">{g.count}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </Card>

      <Card>
        {loading ? (
          <Loading message="Loading team members..." />
        ) : filteredUsers.length === 0 ? (
          <Empty message="No members found" hint={search || roleFilter ? 'Try adjusting your search or filters' : undefined} />
        ) : (
          <Table columns={columns} data={filteredUsers} onRowClick={openUserDetail} />
        )}
      </Card>

      {/* Teams section */}
      <section className="users-page__teams">
        <div className="users-page__teams-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 20, flexWrap: 'wrap', gap: 16 }}>
          <PageHeader title="Teams" subtitle={`${teams.length} team${teams.length !== 1 ? 's' : ''} in your organization`} />
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            <div className="users-page__search-wrap" style={{ margin: 0, minWidth: 260 }}>
              <Search size={16} className="users-page__search-icon" />
              <input
                className="users-page__search"
                placeholder="Search teams by name or desc..."
                value={teamSearch}
                onChange={e => setTeamSearch(e.target.value)}
              />
            </div>
            <div className="users-page__teams-view-toggle">
            <button
              type="button"
              className={`users-page__view-btn${teamViewMode === 'tiles' ? ' users-page__view-btn--active' : ''}`}
              onClick={() => setTeamViewMode('tiles')}
              title="Tiles view"
              aria-label="Tiles view"
            >
              <LayoutGrid size={16} />
              <span>Tiles</span>
            </button>
            <button
              type="button"
              className={`users-page__view-btn${teamViewMode === 'list' ? ' users-page__view-btn--active' : ''}`}
              onClick={() => setTeamViewMode('list')}
              title="List view"
              aria-label="List view"
            >
              <List size={16} />
              <span>List</span>
            </button>
          </div>
        </div>
      </div>

      {loading ? (
          <Loading message="Loading teams..." />
        ) : teams.length === 0 ? (
          <Card><Empty message="No teams created yet" hint="Create a team to organize members into groups" /></Card>
        ) : filteredTeams.length === 0 ? (
          <Card><Empty message="No teams found matching your search." /></Card>
        ) : teamViewMode === 'tiles' ? (
          <div className="users-page__teams-grid">
            {filteredTeams.map(t => {
              const members = t.members || [];
              const stackMembers = members.slice(0, 4);
              const overflow = members.length - stackMembers.length;

              return (
                <Card key={t.team_id} className="users-page__team-card" onClick={() => navigate(`/app/teams/${t.team_id}`)} style={{ cursor: 'pointer' }}>
                  <div className="users-page__team-accent" />
                  <div className="users-page__team-body">
                    <div className="users-page__team-top">
                      <div>
                        <div className="users-page__team-name">{t.name}</div>
                        {t.description && <div className="users-page__team-desc">{t.description}</div>}
                      </div>
                      {t.code && <Badge value={t.code} />}
                    </div>

                    <div className="users-page__team-lead">
                      <Crown size={16} style={{ color: 'var(--accent4)', flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="users-page__team-lead-label">Manager</div>
                        {t.teamLead ? (
                          <>
                            <div className="users-page__team-lead-name">{t.teamLead.name}</div>
                            <div className="users-page__team-lead-title">{t.teamLead.title || t.teamLead.role}</div>
                          </>
                        ) : (
                          <div className="users-page__team-lead-empty">Not assigned</div>
                        )}
                      </div>
                      {t.teamLead && (
                        <Avatar name={t.teamLead.name} size="sm" color="var(--accent)" style={{ borderRadius: '50%' }} />
                      )}
                    </div>

                    <div>
                      <div className="users-page__team-members-head">
                        <div className="users-page__team-members-label">
                          <UsersRound size={13} /> Members
                        </div>
                        {members.length > 0 && (
                          <div className="users-page__avatar-stack">
                            {stackMembers.map((m, i) => (
                              <div
                                key={m.user_id}
                                className="users-page__avatar-stack-item"
                                style={{ background: ROLE_COLORS[m.user?.role] || 'var(--accent)', zIndex: 4 - i }}
                                title={m.user?.name}
                              >
                                {getInitials(m.user?.name)}
                              </div>
                            ))}
                            {overflow > 0 && (
                              <div className="users-page__avatar-stack-more">+{overflow}</div>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="users-page__member-list">
                        {members.length === 0 ? (
                          <span className="users-page__cell-empty">No members assigned</span>
                        ) : (
                          members.map(m => (
                            <div
                              key={m.user_id}
                              className="users-page__member-row users-page__member-row--clickable"
                              onClick={() => openUserDetail(m.user)}
                              role="button"
                              tabIndex={0}
                              onKeyDown={e => e.key === 'Enter' && openUserDetail(m.user)}
                            >
                              <Avatar
                                name={m.user?.name}
                                size="sm"
                                color={ROLE_COLORS[m.user?.role] || 'var(--accent)'}
                                style={{ borderRadius: '50%' }}
                              />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div className="users-page__member-name">{m.user?.name}</div>
                                <div className="users-page__member-meta">{m.user?.title || m.user?.role || '—'}</div>
                              </div>
                              <Badge value={m.user?.role} />
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        ) : (
          /* List view */
          <Card className="users-page__teams-list-card">
            <div className="users-page__teams-list">
              <div className="users-page__teams-list-header">
                <div className="users-page__teams-list-col users-page__teams-list-col--name">Team Name</div>
                <div className="users-page__teams-list-col users-page__teams-list-col--code">Code</div>
                <div className="users-page__teams-list-col users-page__teams-list-col--lead">Manager</div>
                <div className="users-page__teams-list-col users-page__teams-list-col--members">Members</div>
                <div className="users-page__teams-list-col users-page__teams-list-col--desc">Description</div>
              </div>
              {filteredTeams.map(t => {
                const members = t.members || [];
                const stackMembers = members.slice(0, 5);
                const overflow = members.length - stackMembers.length;

                return (
                  <div key={t.team_id} className="users-page__teams-list-row" onClick={() => navigate(`/app/teams/${t.team_id}`)} style={{ cursor: 'pointer' }}>
                    <div className="users-page__teams-list-col users-page__teams-list-col--name">
                      <div className="users-page__teams-list-name-cell">
                        <div className="users-page__teams-list-dot" />
                        <span className="users-page__teams-list-team-name">{t.name}</span>
                      </div>
                    </div>
                    <div className="users-page__teams-list-col users-page__teams-list-col--code">
                      {t.code ? <Badge value={t.code} /> : <span className="users-page__cell-empty">—</span>}
                    </div>
                    <div className="users-page__teams-list-col users-page__teams-list-col--lead">
                      {t.teamLead ? (
                        <div className="users-page__teams-list-lead-cell">
                          <Avatar name={t.teamLead.name} size="sm" color="var(--accent)" style={{ borderRadius: '50%' }} />
                          <div style={{ minWidth: 0 }}>
                            <div className="users-page__teams-list-lead-name">{t.teamLead.name}</div>
                            <div className="users-page__teams-list-lead-title">{t.teamLead.title || t.teamLead.role}</div>
                          </div>
                        </div>
                      ) : (
                        <span className="users-page__cell-empty">Not assigned</span>
                      )}
                    </div>
                    <div className="users-page__teams-list-col users-page__teams-list-col--members">
                      <div className="users-page__teams-list-members-cell">
                        {members.length > 0 && (
                          <div className="users-page__avatar-stack">
                            {stackMembers.map((m, i) => (
                              <div
                                key={m.user_id}
                                className="users-page__avatar-stack-item"
                                style={{ background: ROLE_COLORS[m.user?.role] || 'var(--accent)', zIndex: 5 - i }}
                                title={m.user?.name}
                              >
                                {getInitials(m.user?.name)}
                              </div>
                            ))}
                            {overflow > 0 && (
                              <div className="users-page__avatar-stack-more">+{overflow}</div>
                            )}
                          </div>
                        )}
                        <span className="users-page__teams-list-member-count">
                          {members.length} member{members.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                    <div className="users-page__teams-list-col users-page__teams-list-col--desc">
                      {t.description
                        ? <span className="users-page__teams-list-desc">{t.description}</span>
                        : <span className="users-page__cell-empty">—</span>
                      }
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}
      </section>



      {canUpdate && (
        <Modal open={modal} onClose={() => setModal(false)} title="Edit User">
          <Alert type="error" message={error} />
          <form onSubmit={handleSave}>
            <FormField label="Name"><input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required /></FormField>
            <FormField label="Email"><input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required /></FormField>
            <FormField label="Designation"><input value={form.title || ''} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. Senior Software Engineer" /></FormField>
            <FormField label="Role">
              <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                {ROLES.map(r => <option key={r}>{r}</option>)}
              </select>
            </FormField>
            <FormField label="Gender">
              <select value={form.gender} onChange={e => setForm(f => ({ ...f, gender: e.target.value }))}>
                <option value="">Not Specified</option>
                {['Male', 'Female', 'Other'].map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </FormField>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
              <Button variant="secondary" onClick={() => setModal(false)} type="button">Cancel</Button>
              <Button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Update'}</Button>
            </div>
          </form>
        </Modal>
      )}

      {canCreate && (
        <Modal open={teamModal} onClose={() => setTeamModal(false)} title="Add Team Member">
          <Alert type="error" message={error} />
          <form onSubmit={handleCreate}>
            <FormField label="Name"><input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required /></FormField>
            <FormField label="Email"><input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} /></FormField>
            <FormField label="Unique code"><input value={form.unique_code || ''} onChange={e => setForm(f => ({ ...f, unique_code: e.target.value }))} placeholder="Optional - auto-generated if blank" /></FormField>
            <FormField label="Password"><PasswordInput value={form.password || ''} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder="Optional - default is set if left blank" /></FormField>
            <FormField label="Phone"><input value={form.phone || ''} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} /></FormField>
            <FormField label="Designation"><input value={form.title || ''} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. Senior Software Engineer" /></FormField>
            <FormField label="Role">
              <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                {ROLES.map(r => <option key={r}>{r}</option>)}
              </select>
            </FormField>
            <FormField label="Gender">
              <select value={form.gender} onChange={e => setForm(f => ({ ...f, gender: e.target.value }))}>
                <option value="">Not Specified</option>
                {['Male', 'Female', 'Other'].map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </FormField>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>If you leave email or password blank, the system will generate defaults.</div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
              <Button variant="secondary" onClick={() => setTeamModal(false)} type="button">Cancel</Button>
              <Button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Add member'}</Button>
            </div>
          </form>
        </Modal>
      )}

      {canCreate && (
        <Modal open={createTeamModal} onClose={() => setCreateTeamModal(false)} title="Create Team" width={600}>
          <Alert type="error" message={error} />
          <form onSubmit={handleCreateTeam}>
            <FormField label="Team name"><input value={teamForm.name} onChange={e => setTeamForm(f => ({ ...f, name: e.target.value }))} required /></FormField>
            <FormField label="Description"><textarea value={teamForm.description} onChange={e => setTeamForm(f => ({ ...f, description: e.target.value }))} /></FormField>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <FormField label="Team code (optional)"><input value={teamForm.code} onChange={e => setTeamForm(f => ({ ...f, code: e.target.value }))} /></FormField>
              <FormField label="Manager (optional)">
                <select value={teamForm.team_lead_id} onChange={e => setTeamForm(f => ({ ...f, team_lead_id: e.target.value }))}>
                  <option value="">Select Manager...</option>
                  {users.filter(u => u.role === 'Manager').map(u => (
                    <option key={u.user_id} value={u.user_id}>{u.name} ({u.title || u.role})</option>
                  ))}
                </select>
              </FormField>
            </div>

            <FormField label="Members">
              <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', overflow: 'hidden', background: 'var(--bg-hover)' }}>
                <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                  {users.map(u => (
                    <label
                      key={u.user_id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '10px 12px',
                        borderBottom: '1px solid var(--border)',
                        cursor: 'pointer',
                        transition: 'background 0.15s',
                        background: teamForm.memberIds.includes(u.user_id) ? 'rgba(108,99,255,0.12)' : 'transparent',
                      }}
                      onMouseEnter={e => !teamForm.memberIds.includes(u.user_id) && (e.currentTarget.style.background = 'var(--bg-card)')}
                      onMouseLeave={e => !teamForm.memberIds.includes(u.user_id) && (e.currentTarget.style.background = 'transparent')}
                    >
                      <input
                        type="checkbox"
                        checked={teamForm.memberIds.includes(u.user_id)}
                        onChange={e => {
                          const checked = e.target.checked;
                          setTeamForm(f => ({
                            ...f,
                            memberIds: checked ? [...f.memberIds, u.user_id] : f.memberIds.filter(id => id !== u.user_id),
                          }));
                        }}
                        style={{ cursor: 'pointer', width: 18, height: 18, accentColor: 'var(--accent)' }}
                      />
                      <Avatar name={u.name} size="sm" color={ROLE_COLORS[u.role] || 'var(--accent)'} style={{ borderRadius: '50%' }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{u.name}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>{u.title || u.role} &bull; {u.email || u.unique_code}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
                {teamForm.memberIds.length > 0
                  ? `${teamForm.memberIds.length} member${teamForm.memberIds.length > 1 ? 's' : ''} selected`
                  : 'Select at least one member'}
              </div>
            </FormField>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 20 }}>
              <Button variant="secondary" onClick={() => setCreateTeamModal(false)} type="button">Cancel</Button>
              <Button type="submit" disabled={teamSaving || teamForm.memberIds.length === 0}>{teamSaving ? 'Creating...' : 'Create Team'}</Button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
